import { ShiftsService } from '@api'
import { format } from 'date-fns/format'

import { getLocalDateTime } from '@/utils/date.ts'

import type { FiltersFormValues } from '../ui/filters'

const formatDate = (date: Date) => format(date, 'yyyy-MM-dd')

export const getShifts = async ({
  from,
  to,
}: Pick<FiltersFormValues, 'from' | 'to'>) => {
  const { shifts } = await ShiftsService.getShifts({
    startedAfter: formatDate(from),
    endBefore: formatDate(to),
  })

  // The endpoint always returns the proper weekdays in UTC for repeating shifts
  // We check if the day changes in the local time conversion, and if so we prevent it
  for (const shiftsKey in shifts) {
    for (const shift of shifts[shiftsKey]) {
      if (!shift.repeatableShiftId) {
        continue
      }

      const startedAtUTC = new Date(shift.startedAt)
      const finishedAtUTC = new Date(shift.finishedAt)

      const localStartedAt = getLocalDateTime(shift.startedAt)

      const delta = localStartedAt.getDate() - startedAtUTC.getDate()

      // If the day changes, we move the shift by the offset in milliseconds
      if (delta !== 0) {
        const offset = localStartedAt.getTime() - startedAtUTC.getTime()

        startedAtUTC.setTime(startedAtUTC.getTime() - offset)
        finishedAtUTC.setTime(finishedAtUTC.getTime() - offset)

        shift.startedAt = startedAtUTC.toISOString()
        shift.finishedAt = finishedAtUTC.toISOString()
      }
    }
  }

  return shifts
}

export const getShiftsHistory = async ({
  from,
  to,
}: Pick<FiltersFormValues, 'from' | 'to'>) => {
  return await ShiftsService.getShiftsHistory({
    startDate: formatDate(from),
    endDate: formatDate(to),
  })
}

export const getAccessibleTeamMembersForCreators = async (
  creatorIds: number[],
) => {
  return await ShiftsService.accessibleTeamMembers({
    creatorIds,
  })
}

export const getAvailableCreators = async (memberId: number) => {
  return await ShiftsService.accessibleTargets({
    target: 'TeamMember',
    id: memberId,
  })
}
